import { useMemo, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
    getCategoryFromProductId,
    getAvailableCategories,
    getSortOptionById,
    SORT_OPTIONS
} from '../config/categoryConfig';
import promoConfig from '../config/promoConfig.json';

/**
 * Hook para gestionar filtrado y ordenamiento de productos.
 * Persiste el estado en URL query params para URLs compartibles.
 *
 * @param {Array} products - Lista de productos pre-filtrados (despues de search/promo URL)
 * @returns {Object} - Estado de filtros, productos filtrados y funciones de control
 */
export const useProductFilters = (products) => {
    const [searchParams, setSearchParams] = useSearchParams();

    // Leer estado de filtros desde URL
    const activeCategory = searchParams.get('category') || null;
    const activeSortId = searchParams.get('sort') || 'default';
    const promoOnly = searchParams.get('promoOnly') === 'true';

    // Obtener configuracion de ordenamiento
    const activeSort = getSortOptionById(activeSortId);

    // Obtener categorias disponibles (solo las que tienen productos)
    const availableCategories = useMemo(() => {
        return getAvailableCategories(products);
    }, [products]);

    // Filtrar por categoria
    const categoryFilteredProducts = useMemo(() => {
        if (!activeCategory) return products;

        return products.filter(product => {
            const productCategory = getCategoryFromProductId(product.id);
            return productCategory && productCategory.id === activeCategory;
        });
    }, [products, activeCategory]);

    // Filtrar por promocion activa (toggle)
    const promoFilteredProducts = useMemo(() => {
        if (!promoOnly) return categoryFilteredProducts;

        // Solo mostrar productos que coincidan con el prefijo de promo activa
        if (!promoConfig.enabled || !promoConfig.productPrefix) {
            return categoryFilteredProducts;
        }

        return categoryFilteredProducts.filter(product =>
            product.id.startsWith(promoConfig.productPrefix)
        );
    }, [categoryFilteredProducts, promoOnly]);

    // Ordenar productos
    const sortedProducts = useMemo(() => {
        if (!activeSort.field) return promoFilteredProducts;

        return [...promoFilteredProducts].sort((a, b) => {
            const aVal = a[activeSort.field];
            const bVal = b[activeSort.field];

            if (activeSort.order === 'asc') {
                return aVal - bVal;
            }
            return bVal - aVal;
        });
    }, [promoFilteredProducts, activeSort]);

    // Helper para actualizar params manteniendo los existentes
    const updateParams = useCallback((updates) => {
        const newParams = new URLSearchParams(searchParams);

        Object.entries(updates).forEach(([key, value]) => {
            if (value === null || value === undefined || value === 'default' || value === false || value === '') {
                newParams.delete(key);
            } else {
                newParams.set(key, String(value));
            }
        });

        setSearchParams(newParams);
    }, [searchParams, setSearchParams]);

    // Funciones de control
    const setCategory = useCallback((categoryId) => {
        updateParams({ category: categoryId });
    }, [updateParams]);

    const setSort = useCallback((sortId) => {
        updateParams({ sort: sortId === 'default' ? null : sortId });
    }, [updateParams]);

    const togglePromoOnly = useCallback(() => {
        updateParams({ promoOnly: !promoOnly ? 'true' : null });
    }, [updateParams, promoOnly]);

    const clearAllFilters = useCallback(() => {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('category');
        newParams.delete('sort');
        newParams.delete('promoOnly');
        setSearchParams(newParams);
    }, [searchParams, setSearchParams]);

    // Verificar si hay filtros activos
    const hasActiveFilters = activeCategory || activeSortId !== 'default' || promoOnly;

    // Verificar si hay una promocion disponible para el toggle
    const isPromoAvailable = promoConfig.enabled && promoConfig.productPrefix;

    return {
        // Productos filtrados y ordenados
        filteredProducts: sortedProducts,

        // Estado actual de filtros
        activeCategory,
        activeSort,
        activeSortId,
        promoOnly,
        hasActiveFilters,

        // Funciones de control
        setCategory,
        setSort,
        togglePromoOnly,
        clearAllFilters,

        // Opciones disponibles para UI
        availableCategories,
        sortOptions: SORT_OPTIONS,
        isPromoAvailable
    };
};

export default useProductFilters;
